import {
  FormControl,
  FormHelperText,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  SxProps,
  Theme,
  Autocomplete,
  TextField,
  Paper,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { COLORS } from "../../assets/theme";
import CustomLabel from "./CustomLabel";
import CustomCheckbox from "../CustomCheckbox";
import CustomTextField from "./CustomTextField";

type DropdownOption = { label: string; value: string | number };

export type ICustomSelectProps = {
  label?: string;
  labelComponent?: React.ReactNode; // Add this new prop
  value: string | number | boolean | (string | number)[];
  options: DropdownOption[];
  onChange: (value: string | number | (string | number)[]) => void;
  onBlur?: (value: string | number | (string | number)[]) => void;
  placeholder?: string;
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  bgColor?: string;
  multiSelect?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  highlightLabelStar?: boolean;
  clearable?: boolean;
  labelFontWeight?: number | string;
  labelFontSize?: string | number;
  sx?: SxProps<Theme>;
  xs?: number;
  sm?: number;
  md?: number;
  minWidth?: number;
  lg?: number;
  renderPlaceholder?: boolean;
  selectAll?: boolean;
  searchable?: boolean;
  textable?: boolean;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  searchPlaceholder?: string;
  isDeleted?: boolean;
  onToggleDeleted?: () => void;
  inSideBorderRadius?: string;
};

const StyledSelect = styled(Select)<{ $bgColor?: string; $disabled?: boolean }>(
  ({ $bgColor, $disabled }) => ({
    backgroundColor: $disabled ? "#f0f0f0" : $bgColor || "transparent",
    borderRadius: "12px",
    "& .MuiSelect-select": {
      padding: "8px 12px",
      color: $disabled ? "#c0c0c0" : "#000000",
      fontSize: "1rem",
      borderRadius: "12px",
    },
    "& .placeholder": {
      color: "#868585",
      borderRadius: "0px",
      fontSize: "0.85rem",
    },
    "& .MuiListSubheader-root": {
      position: "sticky",
      top: 0,
      zIndex: 1,
      backgroundColor: "white",
    },
  })
);

const StyledTextField = styled(TextField)<{
  $bgColor?: string;
  $disabled?: boolean;
  inSideBorderRadius?: string;
}>(({ $bgColor, $disabled, theme, inSideBorderRadius }) => ({
  backgroundColor: $disabled ? "#f0f0f0" : $bgColor || "transparent",
  borderRadius: inSideBorderRadius ?? "12px",
  "& .MuiOutlinedInput-root": {
    borderRadius: inSideBorderRadius ?? "12px",
    "& input": {
      padding: "8px 12px",
      color: $disabled ? "#c0c0c0" : "#000000",
      fontSize: "1rem",
    },
    "& fieldset": {
      borderColor: theme.palette.mode === "light" ? "#e0e0e0" : "#2c2c2c",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .placeholder": {
    color: "#868585",
    fontSize: "0.85rem",
  },
}));
const StyledFormControl = styled(FormControl)<{
  $size: "small" | "medium" | "large";
  $fullWidth?: boolean;
  $minWidth?: string | number;
}>(({ $size, $fullWidth, $minWidth, theme }) => ({
  borderRadius: "12px",
  width: $fullWidth
    ? "100%"
    : $size === "small"
    ? "250px"
    : $size === "medium"
    ? "368px"
    : "463px",
  minWidth: $minWidth || "auto",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minWidth: 0,
  },
}));

const CustomSelect: React.FC<ICustomSelectProps> = ({
  label,
  value,
  options,
  onChange,
  onBlur,
  placeholder = "Select an option",
  size = "small",
  fullWidth = false,
  error = false,
  helperText = "",
  bgColor = "transparent",
  multiSelect = false,
  disabled = false,
  highlightLabelStar = false,
  labelFontWeight = "500",
  labelFontSize = "0.85rem",
  sx = {},
  labelComponent,
  minWidth,
  renderPlaceholder = false,
  selectAll = false,
  searchable = false,
  textable = false,
  searchValue: externalSearchValue,
  onSearchValueChange: externalOnSearchValueChange,
  searchPlaceholder = "Search...",
  isDeleted = false,
  onToggleDeleted,
  inSideBorderRadius, // <-- Add this line
}) => {
  const theme = useTheme();
  const SELECT_ALL_VALUE = "__select_all__";
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (selectRef.current) {
      setMenuWidth(selectRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (selectRef.current) {
        setMenuWidth(selectRef.current.offsetWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectOpen]);

  const searchValue =
    externalSearchValue !== undefined
      ? externalSearchValue
      : internalSearchValue;

  const allSelected =
    multiSelect && Array.isArray(value) && value.length === options.length;

  const handleSelectAllToggle = (
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent
      | React.SyntheticEvent
  ) => {
    event?.stopPropagation();
    const newValue = allSelected ? [] : options.map((option) => option.value);
    onChange(newValue);
  };

  const toggleValueInArray = (
    currentValues: (string | number)[],
    valueToToggle: string | number
  ): (string | number)[] => {
    return currentValues.includes(valueToToggle)
      ? currentValues.filter((v) => v !== valueToToggle)
      : [...currentValues, valueToToggle];
  };

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    onChange(e.target.value as string | number | (string | number)[]);
  };

  const computedValue = multiSelect
    ? Array.isArray(value)
      ? value
      : []
    : value;

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const finalOptions = searchValue ? filteredOptions : options;

  const renderValue = (selected: unknown) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return <CustomLabel text={placeholder} color={COLORS.placeHolderColor} />;
    }
    if (Array.isArray(selected)) {
      const selectedValues = selected.filter((val) => val !== SELECT_ALL_VALUE);

      if (selectedValues.length === options.length) {
        return "All Selected";
      }
      return selectedValues
        .map(
          (val) =>
            options.find((option) => option.value.toString() === val.toString())
              ?.label
        )

        .join(", ");
    }
    return (
      options.find((option) => option.value.toString() === selected.toString())
        ?.label || placeholder
    );
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent,
    value: DropdownOption | DropdownOption[] | null
  ) => {
    if (value) {
      if (Array.isArray(value)) {
        onChange(value.map((option) => option.value));
      } else {
        onChange(value.value);
      }
    } else {
      onChange(multiSelect ? [] : "");
    }
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: DropdownOption
  ) => {
    if (multiSelect && selectAll) {
      return (
        <li {...props}>
          <Box sx={{ width: "50px" }}>
            <CustomCheckbox
              checked={Array.isArray(value) && value.includes(option.value)}
              label={option.label}
              onChange={(e) => {
                e.stopPropagation();
                if (Array.isArray(value)) {
                  const newValue = toggleValueInArray(value, option.value);
                  onChange(newValue);
                }
              }}
            />
          </Box>
        </li>
      );
    }
    return <li {...props}>{option.label}</li>;
  };

  const selectedOptions = multiSelect
    ? options.filter(
        (option) => Array.isArray(value) && value.includes(option.value)
      )
    : options.find((option) => option.value === value) || null;

  return (
    <StyledFormControl
      fullWidth={fullWidth}
      error={error}
      $size={size}
      $fullWidth={fullWidth}
      $minWidth={minWidth}
      disabled={disabled}
      sx={sx}
    >
      {label && (
        <CustomLabel
          text={label}
          color={COLORS.typographyColor}
          highlightLabelStar={highlightLabelStar}
          fontWeight={labelFontWeight}
          fontSize={labelFontSize}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {textable ? (
        <Autocomplete
          multiple={multiSelect}
          options={options}
          
          getOptionLabel={(option) => {
            if (typeof option === "string") return "";
            return option.label;
          }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              placeholder={placeholder}
              error={error}
              $bgColor={bgColor}
              inSideBorderRadius={inSideBorderRadius}
              $disabled={disabled}
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px 0px 0px 12px",
                  height: "40px",
                  [theme.breakpoints.down("sm")]: {
                    borderRadius: "12px",
                    height: "36px",
                  },
                },
                width: { xs: "100%", sm: undefined },
              }}
            />
          )}
          value={selectedOptions}
          onChange={handleOptionSelect}
          disableCloseOnSelect={multiSelect}
          onInputChange={(_, newInputValue) => {
            if (externalOnSearchValueChange) {
              externalOnSearchValueChange(newInputValue);
            } else {
              setInternalSearchValue(newInputValue);
            }
          }}
          renderOption={renderOption}
          filterOptions={(x) => x}
          PaperComponent={(props) => (
            <Paper
              {...props}
              elevation={3}
              sx={{
                marginTop: "4px",
                maxHeight: "300px",
                overflow: "auto",
                width: { xs: "100%", sm: undefined },
                left: 0,
                right: 0,
              }}
            />
          )}
          disabled={disabled}
          sx={{
            ...sx,
            "& .MuiAutocomplete-tag": {
              backgroundColor: COLORS.activeBackground,
              color: "white",
              borderRadius: "8px",
              margin: "2px",

              padding: "2px 6px",
            },
          }}
        />
      ) : (
        <StyledSelect
          value={computedValue}
          onChange={handleChange}
          multiple={multiSelect}
          $bgColor={bgColor}
          $disabled={disabled}
          
          renderValue={renderValue}
          size={size === "large" ? "medium" : size}
          disabled={disabled}
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={() => setSelectOpen(false)}
          sx={{
            ...sx,
            width: { xs: "100%", sm: undefined },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                width: menuWidth ? `${menuWidth}px` : "100%",
                minWidth: "unset",
                maxWidth: "unset",
              },
            },
            sx: {
              "& .MuiPaper-root": {
                maxHeight: "300px",
                overflow: "auto",
                left: 0,
                right: 0,
              },
            },
          }}
          ref={selectRef}
        >
          {onToggleDeleted && (
            <Box
              component={MenuItem}
              role="menuitem"
              sx={{
                px: 2,
                py: 1,
                cursor: "pointer",
                listStyle: "none",
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleDeleted();
                setTimeout(() => setSelectOpen(true), 0);
              }}
            >
              {isDeleted ? "Show Not Deleted" : "Show  Deleted"}
            </Box>
          )}
          {searchable && (
            <ListSubheader
              disableSticky
              sx={{
                backgroundColor: "white",
                borderBottom: "1px solid #eaeaea",
                px: 1,
                pt: 1,
                pb: 0.5,
                lineHeight: "normal",
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
            >
              <CustomTextField
                placeholder={searchPlaceholder}
                autoFocus={true}
                value={searchValue || ""}
                onChange={(e) => {
                  if (externalOnSearchValueChange) {
                    externalOnSearchValueChange(e.target.value);
                  } else {
                    setInternalSearchValue(e.target.value);
                  }
                }}
                fullWidth
                sx={{
                  "& .MuiInputBase-input": {
                    p: "8px",
                    fontSize: "0.875rem",
                    backgroundColor: "transparent",
                    border: "none",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                    borderRadius: "12px 0px 0px 12px",
                  },
                }}
              />
            </ListSubheader>
          )}

          {multiSelect && selectAll && (
            <Box
              sx={{
                color: "#868585",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%",
                px: 2,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAllToggle();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectAllToggle(e);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <CustomCheckbox
                checked={allSelected}
                onChange={handleSelectAllToggle}
                ariaLabel="select all"
              />
              <Box
                component="span"
                sx={{ ml: 1, flexGrow: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAllToggle(e);
                }}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </Box>
            </Box>
          )}
          {renderPlaceholder && (
            <MenuItem value="" sx={{ color: "#868585" }}>
              <em>{placeholder}</em>
            </MenuItem>
          )}
          {finalOptions.map((option, index) => (
            <MenuItem
              key={index}
              value={option.value}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                if (multiSelect) {
                  const current = Array.isArray(computedValue)
                    ? (computedValue as (string | number)[])
                    : [];
                  const newValue = toggleValueInArray(current, option.value);
                  onChange(newValue);
                } else {
                  onChange(option.value);
                  setSelectOpen(false);
                }
              }}
            >
              {multiSelect && selectAll ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <CustomCheckbox
                    checked={
                      Array.isArray(computedValue) &&
                      computedValue.includes(option.value)
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      if (Array.isArray(computedValue)) {
                        const newValue = toggleValueInArray(
                          computedValue,
                          option.value
                        );
                        onChange(newValue);
                      }
                    }}
                    ariaLabel={`Select ${option.label}`}
                  />
                  <Box component="span" sx={{ ml: 1, flexGrow: 1 }}>
                    {option.label}
                  </Box>
                </Box>
              ) : (
                option.label
              )}
            </MenuItem>
          ))}
        </StyledSelect>
      )}

        {labelComponent}
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  );
};

export default CustomSelect;
